/**
 * Aviso depois que a numeração SNCR chega: com número emitido pela Anvisa a
 * receita só vale assinada digitalmente, então não há mais impressão manual.
 */
import React from 'react';
import { Box, Button, Dialog, DialogContent, IconButton, Typography } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  CLOUD_API_THEME,
  cloudApiDialogCancelButtonSx,
  cloudApiDialogPaperSx,
  cloudApiDialogPrimaryButtonSx,
} from '../../pages/whatsappCloudApiTheme';
import { SNCR_TIPO_LABELS, type SncrReceitaTipo } from '../../services/sncrService';

const NOTIFICATION_LETTER: Partial<Record<SncrReceitaTipo, string>> = {
  NRA: 'A',
  NRB: 'B',
  NRB2: 'B2',
};

function describeTipos(tipos: SncrReceitaTipo[]): string {
  const letters = tipos.map((t) => NOTIFICATION_LETTER[t]).filter(Boolean) as string[];
  const parts: string[] = [];
  if (letters.length) parts.push(`Notificação de Receita ${letters.join('/')}`);
  tipos
    .filter((t) => !NOTIFICATION_LETTER[t])
    .forEach((t) => parts.push(SNCR_TIPO_LABELS[t]));
  return parts.join(', ');
}

interface Props {
  open: boolean;
  tipos: SncrReceitaTipo[];
  onCancel: () => void;
  onSign: () => void;
}

const SncrDigitalSignatureRequiredDialog: React.FC<Props> = ({ open, tipos, onCancel, onSign }) => {
  const unique = Array.from(new Set(tipos));
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth PaperProps={{ sx: { ...cloudApiDialogPaperSx, borderRadius: 3 } }}>
      <DialogContent sx={{ p: { xs: 3, sm: 4.5 }, position: 'relative' }}>
        <IconButton onClick={onCancel} aria-label="Fechar" size="small" sx={{ position: 'absolute', top: 12, right: 12 }}>
          <CloseIcon fontSize="small" />
        </IconButton>

        <Typography sx={{ color: '#dc2626', fontWeight: 700, fontSize: '0.7rem', letterSpacing: 0.4, mt: 2 }}>
          MEDICAMENTO CONTROLADO
        </Typography>
        <Typography sx={{ fontSize: '1.3rem', fontWeight: 500, color: CLOUD_API_THEME.navy, mb: 2 }}>
          Assinatura digital obrigatória
        </Typography>
        <Typography variant="body2" sx={{ color: CLOUD_API_THEME.slate, lineHeight: 1.7, mb: 2.5 }}>
          Este receituário contém medicamentos controlados
          {unique.length ? ` (${describeTipos(unique)})` : ''} que exigem assinatura digital certificada.
        </Typography>

        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: CLOUD_API_THEME.navy, mb: 0.75 }}>
          Exigências legais:
        </Typography>
        <Box component="ul" sx={{ pl: 2.5, m: 0, mb: 2.5, color: CLOUD_API_THEME.slate, fontSize: '0.85rem', lineHeight: 1.8 }}>
          <li>Assinatura digital obrigatória</li>
          <li>Numeração SNCR necessária</li>
          <li>Não é possível impressão manual</li>
        </Box>

        <Typography variant="caption" sx={{ color: CLOUD_API_THEME.slate, fontStyle: 'italic', display: 'block', mb: 3.5 }}>
          Conforme Portaria SVS/MS nº 344/1998 e SNCR
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
          <Button onClick={onCancel} sx={{ ...cloudApiDialogCancelButtonSx, bgcolor: '#e5e7eb', '&:hover': { bgcolor: '#d1d5db' } }}>
            Cancelar prescrição
          </Button>
          <Button variant="contained" onClick={onSign} sx={cloudApiDialogPrimaryButtonSx}>
            Assinar digitalmente
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SncrDigitalSignatureRequiredDialog;
